import type { SearchResponse } from '@algolia/client-search';
import invariant from 'invariant';
import { useCallback, useMemo } from 'react';
import { components } from 'react-select';
import type { ContainerProps, InputProps, SingleValue } from 'react-select';
import AsyncSelect from 'react-select/async';
import type { AsyncProps } from 'react-select/async';

import type { OptionType } from '#/components/Select/Select.types';
import { useAppConfig } from '#/config/AppConfigContext';
import { algoliaSearch } from '#/utils/algolia/algoliaSearch';
import type { AlgoliaSearchArgs } from '#/utils/algolia/algoliaSearch';

import { algoliaSearchSelectStyles } from './algoliaSearchSelectStyles';
import type { AlgoliaSearchSelectStyleOptions } from './algoliaSearchSelectStyles';

type PassableReactSelectProps<T> = Pick<
  AsyncProps<OptionType<T>, false, never>,
  | 'filterOption'
  | 'formatOptionLabel'
  | 'getOptionLabel'
  | 'getOptionValue'
  | 'noOptionsMessage'
  | 'onBlur'
  | 'value'
  | 'placeholder'
  | 'autoFocus'
  | 'className'
>;

export type AlgoliaSearchSelectProps<T> = {
  /**
   * A string to dedup different react-select instances
   * This is necessary to avoid SSR mismatch
   */
  instanceId: string;
  qaId?: string;
  disabled?: boolean;
  onChange?: (newValue: SingleValue<OptionType<T>>) => void;
  searchArgs?: Partial<AlgoliaSearchArgs>;
  responseToOptions?: (hits: SearchResponse<T>['hits']) => OptionType<T>[];
  styleOptions?: AlgoliaSearchSelectStyleOptions;
} & PassableReactSelectProps<T>;

function defaultComponents<T>(args: { qaId?: string }) {
  return {
    SelectContainer: ({
      children,
      ...rest
    }: ContainerProps<OptionType<T>, false, never>) => (
      <components.SelectContainer
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...rest}
        innerProps={
          (args.qaId
            ? { 'data-qa-id': args.qaId }
            : {}) as React.HTMLAttributes<HTMLDivElement>
        }
      >
        {children}
      </components.SelectContainer>
    ),
    Input: (props: InputProps<OptionType<T>, false, never>) => (
      <components.Input
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
      />
    ),
  };
}

export function AlgoliaSearchSelect<T>(props: AlgoliaSearchSelectProps<T>) {
  const { appConfig } = useAppConfig();

  const {
    instanceId,
    qaId,
    className,
    placeholder,
    disabled,
    searchArgs,
    value,
    filterOption,
    formatOptionLabel,
    getOptionLabel,
    getOptionValue,
    onChange,
    onBlur,
    noOptionsMessage,
    styleOptions,
    responseToOptions = (hits) =>
      hits.map((hit) => ({ label: hit.objectID, value: hit })),
  } = props;

  const classNames = useMemo(() => {
    return [className, qaId].filter(Boolean).join(' ');
  }, [className, qaId]);

  const loadOptions = useCallback(
    async (inputValue: string) => {
      const query = searchArgs?.query ?? inputValue;
      invariant(searchArgs?.config, 'Algolia config must be provided');
      invariant(query, 'Algolia query must be provided');
      invariant(searchArgs?.indexName, 'Algolia indexName must be provided');
      invariant(searchArgs?.options, 'Algolia options must be provided');

      const { hits } = await algoliaSearch<T>({
        config: searchArgs.config,
        indexName: searchArgs.indexName,
        query,
        options: searchArgs.options,
        analytics: searchArgs?.analytics,
        onApiKeyTimeout: searchArgs?.onApiKeyTimeout,
        isUsingAlgoliaMock: appConfig.algolia.mock,
      });

      return responseToOptions(hits);
    },
    [searchArgs, responseToOptions, appConfig],
  );

  return (
    <AsyncSelect
      instanceId={instanceId}
      value={value}
      onChange={onChange}
      onBlur={onBlur}
      noOptionsMessage={noOptionsMessage}
      filterOption={filterOption}
      formatOptionLabel={formatOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      loadOptions={loadOptions}
      className={classNames}
      placeholder={placeholder}
      isDisabled={disabled}
      components={defaultComponents<T>({ qaId })}
      styles={algoliaSearchSelectStyles<T>(styleOptions)}
    />
  );
}
